/** @jsxImportSource theme-ui */
import React from 'react';
import { Link } from 'react-router-dom';
import { ThemeUIStyleObject } from 'theme-ui';

import theme from 'theme';
import { focusStyles, valignHackBottom } from 'utils/cssHelpers';

import BdIcon, { InsightsIconLiteral } from 'components/_common/BdIcon/BdIcon';
import { IClickable } from 'components/_common/common-props';
import { FlexGroup } from 'components/_common/FlexGroup';
import { Typography } from 'components/_common/Typography';

/* eslint-disable react/jsx-props-no-spreading */
type BdButtonProps = IClickable & {
  type: 'primary' | 'secondary' | 'tertiary';
  text: string;
  buttonType?: 'button' | 'submit';
  link?: string;
  /** If the link is external, set this flag to true for external routing */
  isLinkExternal?: boolean;
  prefixIcon?: InsightsIconLiteral;
  suffixIcon?: InsightsIconLiteral;
  disabled?: boolean;
  /** Enabled styling for darker backgrounds */
  invert?: boolean;
  /** Makes the button hug the content */
  small?: boolean;
  /** Custom CSS class name */
  className?: string;
  /** If true no text shows up on the button, instead becomes the aria-label */
  textAsAriaLabel?: boolean;
};

const BdButton: React.FC<BdButtonProps> = ({
  type,
  text,
  buttonType = 'button',
  link = null,
  isLinkExternal = false,
  prefixIcon = null,
  suffixIcon = null,
  disabled = false,
  invert = false,
  small = false,
  className,
  textAsAriaLabel = false,
  onClick = null,
  ...props
}) => {
  const getTypeStyles = (): ThemeUIStyleObject => {
    switch (type) {
      case 'primary':
        return invert
          ? {
              backgroundColor: theme.colors.white,
              color: theme.colors.deepSea100,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.8}' },
            }
          : {
              backgroundColor: theme.colors.deepSea100,
              color: theme.colors.white,
              '&:hover': { backgroundColor: theme.colors.deepSea90 },
              '&:active': { backgroundColor: theme.colors.deepSea70 },
            };
      case 'secondary':
        return {
          backgroundColor: theme.colors.sea80,
          color: theme.colors.white,
          '&:hover': { backgroundColor: theme.colors.sea90 },
          '&:active': { backgroundColor: theme.colors.sea100 },
        };
      case 'tertiary':
      default:
        return invert
          ? {
              backgroundColor: 'transparent',
              color: theme.colors.white,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
              '&:active': { backgroundColor: 'rgba(255,255,255,0.2)' },
            }
          : {
              backgroundColor: 'transparent',
              color: theme.colors.deepSea100,
              '&:hover': { backgroundColor: theme.colors.sea20 },
              '&:active': { backgroundColor: theme.colors.sea20 },
            };
    }
  };

  const styles: ThemeUIStyleObject = {
    cursor: 'pointer',
    borderRadius: '2px',
    boxSizing: 'border-box',
    border:
      type !== 'tertiary'
        ? 'none'
        : invert
        ? `1.5px solid ${theme.colors.white}`
        : `1.5px solid ${theme.colors.deepSea100}`,
    height: small ? null : '48px',
    padding: small ? '4px 8px' : '14px 16px',
    '&:focus': focusStyles({ invert }),
    ...getTypeStyles(),
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 1,
      backgroundColor: theme.colors.stone10,
      color: theme.colors.stone50,
      border: type === 'tertiary' ? `1.5px solid ${theme.colors.stone20}` : 'none',
    },
  };

  const Button = ({ isExternalLink = false }) => {
    const buttonProps = isExternalLink ? { tabIndex: -1 } : props;

    return (
      <button
        type={buttonType}
        data-testid="basic-button"
        disabled={disabled}
        onClick={onClick}
        className={className}
        sx={styles}
        aria-label={textAsAriaLabel && !isExternalLink ? text : null}
        {...buttonProps}
      >
        <FlexGroup
          gutter="4px"
          alignItems="center"
          prefix={
            prefixIcon && (
              <BdIcon
                data-testid="btn-prefix-icon"
                className="btn-prefix-icon"
                name={prefixIcon}
                bold={!small}
                sx={{
                  ...valignHackBottom('-2px'),
                  maxWidth: small ? '12px' : '14px',
                }}
              />
            )
          }
          suffix={
            suffixIcon && (
              <BdIcon
                data-testid="btn-suffix-icon"
                className="btn-suffix-icon"
                name={suffixIcon}
                bold={!small}
                sx={{
                  ...valignHackBottom('-2px'),
                  maxWidth: small ? '12px' : '14px',
                }}
              />
            )
          }
        >
          {!textAsAriaLabel && (
            <Typography variant="button" sx={{ verticalAlign: 'text-bottom' }} inline nowrap>
              {text}
            </Typography>
          )}
        </FlexGroup>
      </button>
    );
  };

  const ButtonLink = () =>
    isLinkExternal ? (
      <a
        data-testid="basic-button-link"
        href={link}
        tabIndex={0}
        aria-label={textAsAriaLabel ? text : null}
        sx={{ '&:focus': focusStyles(), display: 'contents' }}
        {...props}
      >
        <Button isExternalLink />
      </a>
    ) : (
      <Link
        data-testid="basic-button-link"
        to={link}
        aria-label={textAsAriaLabel ? text : null}
        sx={{ '&:focus': focusStyles(), display: 'contents' }}
        {...props}
      >
        <Button />
      </Link>
    );

  return link?.length && !disabled ? <ButtonLink /> : <Button />;
};

export default BdButton;
